import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Space, Table, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import CustomModal from '../../components/modal';
import { Context } from '../../context/context';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { addMenu, disableRefetch } from '../../redux/slices/menu';
import merchantService from '../../services/merchant';
import { fetchMerchants } from '../../redux/slices/merchant';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import DeleteButton from '../../components/delete-button';
import MerchantStatusModal from './merchant-status-modal';
import SearchInput from '../../components/search-input';

const Merchants = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [merchantStatus, setMerchantStatus] = useState(null);
  const { user } = useSelector((state) => state.auth, shallowEqual);

  const goToEdit = (row) => {
    dispatch(
      addMenu({
        id: 'edit-merchant',
        url: `merchant/${row?.uuid}`,
        name: t('edit.merchant'),
      })
    );
    navigate(`/merchant/${row?.uuid}`);
  };

  const columns = [
    {
      title: t('id'),
      dataIndex: 'id',
      render: (id) => id ?? '-',
    },
    {
      title: t('name'),
      dataIndex: 'name',
      render: (name) => name ?? '-',
    },
    {
      title: t('pharmacy.code'),
      dataIndex: 'pharmacy_code',
      render: (pharmacy_code) => pharmacy_code ?? '-',
    },
    {
      title: t('pharmacist.name'),
      dataIndex: 'pharmacist_name',
      render: (pharmacist_name) => pharmacist_name ?? '-',
    },
    {
      title: t('phone'),
      dataIndex: 'phone',
      render: (phone) => phone ?? '-',
    },
    {
      title: t('governorate'),
      dataIndex: 'governorate',
      render: (governorate) => governorate ?? '-',
    },
    {
      title: t('area'),
      dataIndex: 'area',
      render: (area) => area ?? '-',
    },
    {
      title: t('user'),
      dataIndex: 'user',
      render: (user) =>
        user ? `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || '-' : '-',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      render: (status, row) => (
        <div>
          {status === 'new' ? (
            <Tag color='blue'>{t(status ?? 'new')}</Tag>
          ) : status === 'rejected' ? (
            <Tag color='error'>{t(status)}</Tag>
          ) : status === 'approved' ? (
            <Tag color='success'>{t(status)}</Tag>
          ) : status === 'cancelled' ? (
            <Tag color='warning'>{t(status)}</Tag>
          ) : (
            <Tag color='cyan'>{t(status ?? 'unknown')}</Tag>
          )}
          <EditOutlined onClick={() => setMerchantStatus(row)} />
        </div>
      ),
    },
    {
      title: t('created.at'),
      dataIndex: 'created_at',
      render: (created_at) => created_at ?? '-',
    },
    {
      title: t('options'),
      dataIndex: 'options',
      render: (data, row) => {
        return (
          <Space>
            <Button
              type='primary'
              icon={<EditOutlined />}
              onClick={() => goToEdit(row)}
            />
            {user?.role !== 'manager' ? (
              <DeleteButton
                icon={<DeleteOutlined />}
                onClick={() => {
                  setId(row?.uuid);
                  setIsModalVisible(true);
                }}
              />
            ) : (
              ''
            )}
          </Space>
        );
      },
    },
  ];

  const { setIsModalVisible } = useContext(Context);
  const [id, setId] = useState(null);
  const [search, setSearch] = useState(null);

  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);
  const { merchants, meta, loading, params } = useSelector(
    (state) => state.merchant,
    shallowEqual
  );

  const merchantDelete = () => {
    merchantService.delete(id).then(() => {
      toast.success(t('successfully.deleted'), { autoClose: 10000 });
      setIsModalVisible(false);
      dispatch(fetchMerchants(params));
    });
  };

  const onChangePagination = (pageNumber) => {
    const { pageSize, current } = pageNumber;
    dispatch(fetchMerchants({ perPage: pageSize, page: current, search }));
  };

  const handleSearch = (value) => {
    setSearch(value);
    dispatch(fetchMerchants({ ...params, search: value, page: 1 }));
  };

  useEffect(() => {
    if (activeMenu?.refetch) {
      dispatch(fetchMerchants({ ...params, search }));
      dispatch(disableRefetch(activeMenu));
    }
  }, [activeMenu?.refetch]);

  const goToAdd = () => {
    dispatch(
      addMenu({
        id: 'add-merchant',
        url: `merchant/add`,
        name: t('add.merchant'),
      })
    );
    navigate(`/merchant/add`);
  };

  return (
    <Card
      title={t('merchants')}
      extra={
        <Space>
          <SearchInput
            placeholder={t('search.merchant.placeholder')}
            handleChange={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            icon={<PlusCircleOutlined />}
            type='primary'
            onClick={goToAdd}
          >
            {t('add.merchant')}
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={merchants}
        loading={loading}
        pagination={{
          pageSize: params?.perPage,
          page: params?.page,
          total: meta?.total,
          defaultCurrent: params?.page,
        }}
        rowKey={(record) => record?.uuid}
        onChange={onChangePagination}
        scroll={{ x: 1200 }}
      />
      {merchantStatus && (
        <MerchantStatusModal
          data={merchantStatus}
          handleCancel={() => setMerchantStatus(null)}
        />
      )}
      <CustomModal click={merchantDelete} text={t('delete.merchant')} />
    </Card>
  );
};

export default Merchants;
